import { AxiosInstance } from 'axios';
import { OrderByLiterals, PaginatedQueryParams } from '@trukt/types/PaginatedQueryParams';
import { JobStatusLiterals } from '@trukt/types/Job';
import { SimplePaginatedResults } from '@trukt/types/PaginatedResults';
import { Terminal } from '@trukt/types/Terminal';
import {
  Integration,
  IntegrationStatusLiterals,
  IntegrationAppLiterals,
} from '@trukt/types/Integration';
import { Invoice, InvoiceStatusLiterals, InvoiceTypeLiterals } from '@trukt/types/Invoice';
import { OrderStatusLiterals } from '@trukt/types/Order';
import { CommodityTypeLiterals } from '@trukt/types/Commodity';
import { BaseRequest } from '@trukt/types/BaseRequest';
import { InvoiceBulkService } from './InvoiceBulkService';
import { InvoiceIntegrationService } from './InvoiceIntegrationService';
import { InvoicePaymentService } from './InvoicePaymentService';

export type InvoiceSearchLogisticsTypeLiterals = 'ORDER' | 'JOB';
export type InvoiceSearchTerminal = Pick<
  Terminal,
  'name' | 'street1' | 'street2' | 'state' | 'city' | 'country' | 'zipCode'
>;

export type InvoiceSearchIntegration = Pick<
  Integration,
  'id' | 'app' | 'externalId' | 'status' | 'errorMessage'
>;

export type InvoiceExportRequest = BaseRequest<
  { force?: boolean; reset?: boolean },
  { apps?: IntegrationAppLiterals[] },
  { invoiceGuid: string }
>;

export interface InvoiceSearchResponseItem
  extends Omit<Invoice, 'type' | 'dateCreated' | 'lines' | 'job' | 'order' | 'clientContact'> {
  caseLabels: string[];
  logistics: {
    orderGuid: string;
    jobGuid: string;
    bol: string | null;
    bolUrl: string | null;
    ebolURL: string | null;
    type: InvoiceSearchLogisticsTypeLiterals;
    status: OrderStatusLiterals;
    number: string;
    origin: InvoiceSearchTerminal | null;
    destination: InvoiceSearchTerminal | null;
    lotNumber: string;
    identifier: string;
    commodityType: CommodityTypeLiterals;
    referenceNumber: string | null;
    dateCompleted: string | null;
    classification: string | null;
    subClassification: string | null;
  } | null;
  integrations: InvoiceSearchIntegration[];
}

export interface InvoiceSearchRequestQueryParams extends PaginatedQueryParams {
  type?: InvoiceTypeLiterals;
  exported?: boolean;
}

export type InvoiceSearchRequestDate = {
  type: 'transaction' | 'payment released' | 'completed';
  start: string;
  end: string;
};

export type InvoiceSearchRequestCase = {
  labels?: string[];
  isDeleted?: boolean;
  isResolved?: boolean;
  hasCases?: boolean;
};

export type InvoiceSearchRequestAccount = {
  type: 'client' | 'consignee';
  guid: string;
};

export type InvoiceSearchRequestOrderClassification = string[];
export type InvoiceSearchRequestJobClassification = string[];

export type InvoiceSearchRequestFilters = {
  invoiceStatus?: InvoiceStatusLiterals[];
  orderStatus?: OrderStatusLiterals[];
  jobStatus?: JobStatusLiterals[];
  client?: string[];
  consignee?: string[];
  cases?: InvoiceSearchRequestCase;
  dates?: InvoiceSearchRequestDate[];
  exportedApp?: { app: IntegrationAppLiterals; status: IntegrationStatusLiterals }[];
  orderClassification?: InvoiceSearchRequestOrderClassification;
  jobClassification?: InvoiceSearchRequestJobClassification;
  paymentConfigs?: string[];
};

export type InvoiceSearchRequestSortColumnLiterals =
  | 'invoice_reference_number'
  | 'invoice_status'
  | 'order_number'
  | 'order_reference_number'
  | 'order_status'
  | 'date_completed'
  | 'transaction_date'
  | 'consignee'
  | 'client'
  | 'invoice_total'
  | 'classification'
  | 'sub_classification'
  | 'bill_of_lading'
  | 'origin_address'
  | 'destination_address'
  | 'date_updated';

export type InvoiceSearchRequestSort = {
  order: OrderByLiterals;
  column: InvoiceSearchRequestSortColumnLiterals;
};

export type InvoiceSearchRequestBody = {
  sort?: InvoiceSearchRequestSort;
  search?: string;
  filters: InvoiceSearchRequestFilters;
};

export type InvoiceSearchRequest = BaseRequest<
  InvoiceSearchRequestQueryParams,
  InvoiceSearchRequestBody
>;
export type InvoiceSearchResponse = SimplePaginatedResults<InvoiceSearchResponseItem>;

export type InvoiceGetRequest = Pick<BaseRequest<void, void, { guid: string }>, 'routeParams'>;
export type InvoiceGetResponse = Invoice;

export type InvoiceReleaseRequest = {
  invoiceGuid: string;
};
export type InvoiceReleaseResponse = void;

export type InvoiceUpdateRequestBody = {
  referenceNumber?: string;
  consigneeGuid?: string | null;
  datePaid?: string | null;
  dateInvoiced?: string | null;
  dateClosed?: string | null;
  isValid?: boolean;
  paymentConfigId?: string | null;
  dateTransaction?: string;
};
export type InvoiceUpdateRequest = BaseRequest<
  void,
  InvoiceUpdateRequestBody,
  { invoiceGuid: string }
>;

export type CreateInvoiceRequest = BaseRequest<
  void,
  {
    consigneeGuid?: string | null;
    dateTransaction?: string;
    isInvoice?: boolean;
    isValid?: boolean;
    orderOrJobGuid?: string;
    paymentConfigId?: string;
    type?: 'vendor_credit' | null;
    linkBills: string[];
  }
>;
export type CreateInvoiceResponse = { guid: string };

export type InvoiceAddLineRequest = BaseRequest<
  { force?: boolean },
  {
    itemId: string;
    amount?: string;
    notes?: string;
    commodityGuid?: string;
  },
  { invoiceGuid: string }
>;
export type InvoiceAddLineResponse = { guid: string };

export class InvoiceService {
  instance: AxiosInstance;
  bulk: InvoiceBulkService;
  integration: InvoiceIntegrationService;
  payment: InvoicePaymentService;
  constructor(instance: AxiosInstance) {
    this.instance = instance;
    this.bulk = new InvoiceBulkService(instance);
    this.integration = new InvoiceIntegrationService(instance);
    this.payment = new InvoicePaymentService(instance);
  }

  search(request: InvoiceSearchRequest) {
    return this.instance.post<InvoiceSearchResponse>('/invoices/list', request.body, {
      params: request.queryParams,
    });
  }

  get(request: InvoiceGetRequest) {
    return this.instance.get<InvoiceGetResponse>(`/invoices/${request.routeParams.guid}`);
  }

  export(request: InvoiceExportRequest) {
    return this.instance.put<Invoice>(
      `/invoices/${request.routeParams.invoiceGuid}/export`,
      request.body,
      {
        params: request.queryParams,
      },
    );
  }

  release({ invoiceGuid }: InvoiceReleaseRequest) {
    return this.instance.put<InvoiceReleaseResponse>(`/invoices/${invoiceGuid}/payment-release`);
  }

  update(request: InvoiceUpdateRequest) {
    return this.instance.patch(`/invoices/${request.routeParams.invoiceGuid}`, request.body);
  }

  create(request: CreateInvoiceRequest) {
    return this.instance.post<CreateInvoiceResponse>('/invoices', request.body);
  }

  addLine({ queryParams, routeParams, body }: InvoiceAddLineRequest) {
    return this.instance.post<InvoiceAddLineResponse>(
      `/invoices/${routeParams.invoiceGuid}/lines`,
      body,
      { params: queryParams },
    );
  }
}
